import React, { useEffect, useState } from "react";
import { X, MagnifyingGlass, Crosshair, MapPin, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { searchCity, reverseGeocode } from "@/lib/weather";

function useCitySearch(q) {
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      const r = await searchCity(q);
      if (!cancelled) {
        setResults(r);
        setBusy(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);
  return { results, busy };
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
  });
}

export const PlantLocationDialog = ({ plant, onClose, onSave }) => {
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const { results, busy } = useCitySearch(q);
  const hasLocation = plant.latitude != null && plant.longitude != null;

  const save = async (body) => {
    setSaving(true);
    try {
      await onSave(body);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const useGps = async () => {
    setSaving(true);
    try {
      const pos = await getPosition();
      const { latitude, longitude } = pos.coords;
      const label = await reverseGeocode(latitude, longitude);
      await save({ latitude, longitude, place_label: label || `${latitude.toFixed(3)}, ${longitude.toFixed(3)}` });
    } catch {
      toast.error("Couldn't read your location. Check location permission.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="plant-location-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-sand-50 rounded-3xl p-6 max-w-md w-full border border-sand-200">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-3xl">Plant location</h2>
          <button type="button" onClick={onClose} className="text-mute hover:text-ink" data-testid="plant-location-close"><X size={20} /></button>
        </div>
        <p className="text-sm text-mute mb-4">
          Where does <span className="text-ink">{plant.nickname || plant.common_name}</span> live? Its forecast risk uses this spot.
        </p>
        {hasLocation && (
          <div className="mb-4 bg-forest/5 border border-forest/20 rounded-xl p-3 flex items-center gap-2 text-sm" data-testid="plant-location-current">
            <MapPin size={14} weight="duotone" className="text-forest shrink-0" />
            <span className="flex-1 truncate">{plant.place_label || `${plant.latitude.toFixed(3)}, ${plant.longitude.toFixed(3)}`}</span>
            <button type="button" disabled={saving} onClick={() => save({ clear_location: true })} className="text-mute hover:text-terra text-xs inline-flex items-center gap-1" data-testid="plant-location-clear">
              <Trash size={12} /> remove
            </button>
          </div>
        )}
        <button type="button" disabled={saving} onClick={useGps} className="btn-secondary w-full justify-center !text-sm" data-testid="plant-location-gps">
          <Crosshair size={14} weight="bold" /> {saving ? "Saving…" : "Use my current location"}
        </button>
        <div className="mt-4 relative">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a city or town…"
            data-testid="plant-location-search"
            className="w-full bg-white border border-sand-200 rounded-xl pl-10 pr-4 py-3 outline-none focus:ring-2 focus:ring-forest"
          />
        </div>
        <div className="mt-2 max-h-56 overflow-y-auto" data-testid="plant-location-results">
          {busy && <div className="font-mono-data text-mute text-xs px-1 py-2 animate-pulse-soft">searching…</div>}
          {!busy && q.trim().length >= 2 && results.length === 0 && (
            <div className="text-mute text-xs px-1 py-2">No places found.</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${i}-${r.latitude},${r.longitude}`}
              type="button"
              disabled={saving}
              onClick={() => save({ latitude: r.latitude, longitude: r.longitude, place_label: r.label })}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-sand-100 text-sm flex items-center gap-2 transition-colors"
              data-testid={`plant-location-result-${i}`}
            >
              <MapPin size={14} weight="duotone" className="text-forest shrink-0" />
              <span className="truncate">{r.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
